import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { instructorLedUnsubscribeFromReminders } from "@/services/api/instructor-led/instructor-led";

type UnsubscribeState = "pending" | "done" | "invalid" | "failed";

export const Route = createFileRoute("/_marketing/ilt-reminders/unsubscribe")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
  component: UnsubscribeFromIltRemindersPage,
});

/**
 * Where the unsubscribe link in a face-to-face session reminder leads (LMS-147). Opening it turns that learner's
 * session reminders off, signed in or not: the signed token in the link says whose. Emails about their bookings still
 * go out, and they can turn reminders back on in their profile.
 */
function UnsubscribeFromIltRemindersPage() {
  const { token } = Route.useSearch();
  // The outcome is the token's it was answered for: a different link opened in place (the same page, a new token) is
  // pending until its own answer comes back, never shown the previous link's outcome.
  const [result, setResult] = useState<{ token: string; state: UnsubscribeState } | null>(null);
  // Once per token: StrictMode runs effects twice in development (the API would answer the same either way).
  const sentForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token || sentForRef.current === token) return;
    sentForRef.current = token;

    instructorLedUnsubscribeFromReminders({ token })
      .then(() => setResult({ token, state: "done" }))
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        setResult({ token, state: status === 400 ? "invalid" : "failed" });
      });
  }, [token]);

  const state: UnsubscribeState = !token
    ? "invalid"
    : result?.token === token
      ? result.state
      : "pending";

  return (
    <section
      aria-labelledby="ilt-reminders-unsubscribe-heading"
      aria-busy={state === "pending"}
      className="bg-arc-surface py-section-sm md:py-section"
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 text-center md:px-8">
        <p className="text-eyebrow text-arc-primary-900">Session reminders</p>
        <h1
          id="ilt-reminders-unsubscribe-heading"
          className="text-display-sm md:text-display-md text-arc-gray-900 font-semibold"
        >
          {HEADINGS[state]}
        </h1>
        <div role="status" className="flex flex-col items-center gap-4">
          {state === "pending" && (
            <p className="text-lead text-arc-gray-600">Turning off your session reminders…</p>
          )}
          {state === "done" && (
            <p className="text-lead text-arc-gray-600">
              You won&apos;t get reminder emails before your face-to-face training sessions.
              You&apos;ll still get emails about your bookings, such as confirmations, changes and
              cancellations.
            </p>
          )}
          {state === "invalid" && (
            <p className="text-lead text-arc-gray-600">
              This unsubscribe link isn&apos;t valid. It may have been changed or copied
              incompletely. You can turn session reminders off in your profile instead.
            </p>
          )}
          {state === "failed" && (
            <p className="text-lead text-arc-gray-600">
              We couldn&apos;t turn off your session reminders just now. Please open the link again
              in a few minutes, or turn them off in your profile.
            </p>
          )}
        </div>
        {state !== "pending" && (
          <Link
            to="/profile"
            className="text-arc-primary-900 hover:text-arc-gray-900 text-md inline-flex min-h-11 items-center font-semibold underline underline-offset-4"
          >
            {state === "done" ? "Turn reminders back on in your profile" : "Go to your profile"}
          </Link>
        )}
      </div>
    </section>
  );
}

const HEADINGS: Record<UnsubscribeState, string> = {
  pending: "Unsubscribing",
  done: "You're unsubscribed from session reminders",
  invalid: "This link isn't valid",
  failed: "Something went wrong",
};
